import { ActionIcon, Box, Group, useMantineTheme } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import type { Nullable } from 'types/utility-types';

import type { AutomationAction } from '../../../../api';
import { useAutomationWizardController } from '../../controller';

import { AssignAccountManager } from './AssignAccountManager';
import { AssignRecoveryPlan } from './AssignRecoveryPlan';
import { ChooseActionType } from './ChooseActionType';
import { CopyFieldToField } from './CopyFieldToField';
import { CopyValueToField } from './CopyValueToField';
import { Pause } from './Pause';
import type { ActionCardProps } from './types';
import { Unpause } from './Unpause';

export type { ActionCardProps };

// Action type to component mapping
const actionComponentMap = {
  selectAction: ChooseActionType,
  CopyValueToField,
  CopyFieldToField,
  AssignRecoveryPlan,
  AssignAccountManager,
  Pause,
  Unpause,
} as const;

export const ActionCard = ({ actionIndex }: ActionCardProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    form,
    actions: { actionsCount, removeAction },
  } = useAutomationWizardController();

  // The action will be undefined while the exit animation is executing
  const action = form.values.actions[actionIndex] as Nullable<AutomationAction> | undefined;

  const ActionComponent = actionComponentMap[action?.type ?? 'selectAction'];

  return (
    <Group
      bg={theme.colors.purple[1]}
      mb="md"
      bdrs={8}
      p="lg"
      pt="sm"
      gap="md"
      style={{ position: 'relative' }}
    >
      {actionsCount !== 1 && (
        <Box style={{ position: 'absolute', top: 6, right: 6 }}>
          <ActionIcon
            aria-label={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.REMOVE_ACTION)}
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() => removeAction(actionIndex)}
          >
            <IconX size={16} color="var(--mantine-color-gray-6)" />
          </ActionIcon>
        </Box>
      )}
      {ActionComponent ? <ActionComponent actionIndex={actionIndex} /> : null}
    </Group>
  );
};
